import { Lender } from '@1delta/lender-registry'
import { Chain } from '@1delta/chain-registry'
import { Call } from '../../../utils/multicall'
import { morphoPools } from '@1delta/data-sdk'
import { fetchListaBrokerUserData } from '../../public-data/lista/listaBroker'
import lodash from 'lodash'

// pull the functions you need
const { chunk } = lodash

export const MORPHO_LENS: { [c: string]: string } = {
  [Chain.HEMI_NETWORK]: '0x1170Ef5B1A7f9c4F0ce34Ddf66CC0e6090Fd107E',
  [Chain.BASE]: '0x05f3f58716a88A52493Be45aA0871c55b3748f18',
  [Chain.POLYGON_MAINNET]: '0x04102873b1A80647879Aa8B8a119F07aE08f457a',
  [Chain.OP_MAINNET]: '0x61895aEB0a42679E2Df8EE64334C405a8d47D244',
  [Chain.ARBITRUM_ONE]: '0xeaC918F73Ba5b11D21D31a72BD00ca4A22865C3f',
  [Chain.KATANA]: '0xCe434378adacC51d54312c872113D687Ac19B516',
  [Chain.HYPEREVM]: '0x6Bc6aCB905c1216B0119C87Bf9E178ce298310FA',
  [Chain.SONEIUM]: '0x4b5458BB47dCBC1a41B31b41e1a8773dE312BE9d',
  [Chain.ETHEREUM_MAINNET]: '0xFc107f469A92c0de7B3105B802584CD6c7D710C2',
  [Chain.PLASMA_MAINNET]: '0xcB6Eb8df68153cebF60E1872273Ef52075a5C297',
  [Chain.BERACHAIN]: '0x7a59ddbB76521E8982Fa3A08598C9a83b14A6C07',
  // Serves BOTH Morpho Blue and Lista on 56 — one lens exposes
  // `get{,Lista,Moolah}{Market,User}DataCompact`. Byte-identical to the
  // previous entry 0x79f4061B… (same keccak), so Lista is unaffected.
  [Chain.BNB_SMART_CHAIN_MAINNET]: '0x7812227c90D55Cc1620C425980141e60b7635824',
  [Chain.UNICHAIN]: '0xA453ba397c61B0c292EA3959A858821145B2707F',
  [Chain.SEI_NETWORK]: '0xcB6Eb8df68153cebF60E1872273Ef52075a5C297',
  [Chain.MONAD_MAINNET]: '0x0bd7473CbBf81d9dD936c61117eD230d95006CA2',
  [Chain.CELO_MAINNET]: '0x4b5458BB47dCBC1a41B31b41e1a8773dE312BE9d',
  [Chain.PLUME_MAINNET]: '0x8E24CfC19c6C00c524353CB8816f5f1c2F33c201',
  [Chain.FLARE_MAINNET]: '0x8E24CfC19c6C00c524353CB8816f5f1c2F33c201',
  [Chain.PHAROS_MAINNET]: '0x8E24CfC19c6C00c524353CB8816f5f1c2F33c201',
  [Chain.ROBINHOOD_CHAIN]: '0x8E24CfC19c6C00c524353CB8816f5f1c2F33c201',
  [Chain.STABLE_MAINNET]: '0x8E24CfC19c6C00c524353CB8816f5f1c2F33c201',
  [Chain.LISK]: '0x8E24CfC19c6C00c524353CB8816f5f1c2F33c201',
  [Chain.ABSTRACT]: '0x8e24cfc19c6c00c524353cb8816f5f1c2f33c201',
  [Chain.INK]: '0x0bd7473CbBf81d9dD936c61117eD230d95006CA2',
  [Chain.KAIA_MAINNET]: '0x925716D57c842B50806884EDb295bA3E3A8EBdFE',
  // blue-api does not index these four, so the lens is the ONLY way to read
  // their Morpho markets — see `lenderApiOnly` in fetchLenderAll.ts.
  [Chain.GNOSIS]: '0x98542B95B44a4732EffB1F4F01A696F1546236d3',
  // Same address as Gnosis — same deployer + nonce.
  [Chain.XDC_NETWORK]: '0x98542B95B44a4732EffB1F4F01A696F1546236d3',
  [Chain.AVALANCHE_C_CHAIN]: '0x2eA75bBBc5615651efc9401FBc9Db4d03B055CA6',
  // Same address as HyperEVM — same deployer + nonce, so CREATE lands
  // identically on both chains.
  [Chain.MEGAETH_MAINNET]: '0x6Bc6aCB905c1216B0119C87Bf9E178ce298310FA',
  // Deployed 2026-08-03. Same reason as the block above — blue-api indexes
  // none of these, so the lens is the only read path. Sonic and Morph already
  // had markets in the registry (9 and 1) that were silently unreadable.
  [Chain.SONIC_MAINNET]: '0xd184c5315B728c1C990f59dDD275c8155f8e255c',
  [Chain.CRONOS_MAINNET]: '0x79f4061BF049c5c6CAC6bfe2415c2460815F4ac7',
  [Chain.MORPH]: '0x79f4061BF049c5c6CAC6bfe2415c2460815F4ac7',
  [Chain.MODE]: '0x2eA75bBBc5615651efc9401FBc9Db4d03B055CA6',
  [Chain.LINEA]: '0x2eA75bBBc5615651efc9401FBc9Db4d03B055CA6',
  [Chain.SCROLL]: '0x4aEA1CE479BF7E036bBB6826A2bF084bce6560a0',
}

export const buildMorphoUserCallWithLens = (
  chainId: string,
  account: string,
  lender: string,
  marketsToQuery: string[],
): Call[] => {
  // 100 per call
  const marketSlices = chunk(marketsToQuery, 100)
  return marketSlices.flatMap((marketsInChunk) => ({
    address: MORPHO_LENS[chainId],
    name: 'getUserDataCompact',
    params: [
      lender === Lender.MORPHO_BLUE
        ? marketsInChunk.map((a) => a.replace('MORPHO_BLUE_', '0x'))
        : marketsInChunk.map((a) => a.replace('LISTA_DAO_', '0x')),
      account,
      // @ts-ignore
      morphoPools()?.[lender]?.[chainId],
    ],
  }))
}

export const buildMorphoTypeUserCallWithLens = (
  chainId: string,
  account: string,
  lender: string,
  marketsToQuery: string[],
  getClient?: (chainId: string, rpcId?: number) => any,
): Call[] | Promise<Call[]> => {
  return lender === Lender.MORPHO_BLUE
    ? buildMorphoUserCallWithLens(chainId, account, lender, marketsToQuery)
    : buildListaUserCallWithLens(
        chainId,
        account,
        lender,
        marketsToQuery,
        getClient,
      )
}

export const buildListaUserCallWithLens = async (
  chainId: string,
  account: string,
  lender: string,
  marketsToQuery: string[],
  getClient?: (chainId: string, rpcId?: number) => any,
): Promise<Call[]> => {
  // Warm the broker per-user cache (fixed loans + dynamic + total debt) so the sync
  // converter can attach the fixed-term breakdown. Runs as a side multicall — it does NOT
  // change the lens call layout, so the slice-based parse stays aligned. Best-effort.
  // `getClient` (when provided) routes the broker reads to the SAME RPC as the lens multicall
  // (e.g. a fork), so a loan created on that RPC is visible — otherwise the default client reads
  // the configured mainnet RPC and a fork-only loan is missed.
  await fetchListaBrokerUserData(
    chainId,
    account,
    marketsToQuery,
    undefined,
    getClient,
  ).catch(() => ({}))

  // 100 per call
  const marketSlices = chunk(marketsToQuery, 100)
  return marketSlices.flatMap((marketsInChunk) => ({
    address: MORPHO_LENS[chainId],
    name: 'getListaUserDataCompact',
    params: [
      marketsInChunk.map((a) => a.replace('LISTA_DAO_', '0x')),
      account,
      // @ts-ignore
      morphoPools()?.[lender]?.[chainId],
    ],
  }))
}
